import './ExportReport.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, Cell,
} from 'recharts';
import {
  buildDetailedReport,
  buildExecutiveReport,
  buildComparativeReport,
  buildMonthlyReport,
} from './pdfReports';

const PRECIO_OFICIAL = 7.50;

const TOOLTIP_STYLE = {
  backgroundColor: '#ffffff',
  border: '1px solid #e8eaf0',
  borderRadius: '8px',
  color: '#1c2136',
  fontSize: '12px',
  boxShadow: '0 2px 12px rgba(28,33,54,0.08)',
};

const BAR_COLORS = ['#2962ff', '#17c671', '#f5a623'];

// Hidden chart dimensions — fixed so Recharts can render off-screen
const CHART_W = 760;
const CHART_H = 220;

const REPORT_CARDS = [
  {
    id: 'executive',
    icon: '📊',
    title: 'Reporte Ejecutivo',
    desc: '1 página · KPIs clave + gráfico principal + observaciones automáticas. Ideal para enviar a un cliente o decisor en segundos.',
    accent: '#2962ff',
    needs: 'single',
  },
  {
    id: 'detailed',
    icon: '📋',
    title: 'Reporte Detallado',
    desc: 'Multi-página · Mensual + semanal + histórico día por día con tablas completas y gráficos. Para análisis profundo.',
    accent: '#17c671',
    needs: 'single',
  },
  {
    id: 'comparative',
    icon: '⚖️',
    title: 'Comparativo Norte vs Sur',
    desc: 'Spread entre las dos zonas, KPIs lado a lado, tabla día a día con diferencias. Para arbitraje y logística.',
    accent: '#1c2136',
    needs: 'both',
  },
  {
    id: 'monthly',
    icon: '📅',
    title: 'Boletín Mensual',
    desc: 'Newsletter del mes con comparación vs mes anterior, mejor/peor día, insights y tendencia. Estilo informe ejecutivo.',
    accent: '#f5a623',
    needs: 'monthly',
  },
  {
    id: 'excel',
    icon: '📈',
    title: 'Excel con Estilo',
    desc: 'Libro con dos hojas: Resumen (KPIs + período) y Datos (tabla zebra con formato moneda). Listo para análisis o presentación.',
    accent: '#107c41',
    needs: 'excel',
  },
];

const ZONE_LABELS = { norte: 'Norte', sur: 'Sur' };

const formatChartDate = (date) =>
  new Date(date).toLocaleDateString('es-EC', { month: 'short', day: 'numeric' });

const ExportReport = () => {
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [zone, setZone]           = useState('norte');

  // Data caches per zone
  const [dataN, setDataN] = useState({ monthly: null, weekly: [], range: [] });
  const [dataS, setDataS] = useState({ monthly: null, weekly: [], range: [] });

  const [fetching, setFetching]       = useState(false);
  const [loadingCard, setLoadingCard] = useState(null);
  const [error, setError]             = useState(null);
  const [success, setSuccess]         = useState(null);

  // Chart refs — single (active zone) and dual (Norte/Sur for comparative)
  const monthlyChartRef = useRef(null);
  const weeklyChartRef  = useRef(null);
  const rangeChartRef   = useRef(null);
  const rangeChartRefN  = useRef(null);
  const rangeChartRefS  = useRef(null);

  const baseUrl = process.env.REACT_APP_API_URL;

  const fetchZoneData = useCallback(async (z) => {
    const token = localStorage.getItem('access_token');
    if (!token) throw new Error(t('auth_required'));
    const config = {
      headers: { Authorization: `Bearer ${token}` },
      params: { start_date: startDate, end_date: endDate, zone: z },
    };
    const [mRes, wRes, rRes] = await Promise.all([
      axios.get(`${baseUrl}/prices/monthly-summary/`, config),
      axios.get(`${baseUrl}/prices/weekly-summary/`,  config),
      axios.get(`${baseUrl}/prices/range/`,           config),
    ]);
    return { monthly: mRes.data, weekly: wRes.data, range: rRes.data };
  }, [startDate, endDate, baseUrl, t]);

  // Fetch active zone data on dates/zone change
  useEffect(() => {
    if (!startDate || !endDate) {
      setDataN({ monthly: null, weekly: [], range: [] });
      setDataS({ monthly: null, weekly: [], range: [] });
      setError(null);
      return;
    }
    setFetching(true);
    setError(null);
    fetchZoneData(zone)
      .then(d => {
        if (zone === 'norte') setDataN(d); else setDataS(d);
      })
      .catch(err => {
        console.error(err);
        setError(t('export_error_fetch'));
      })
      .finally(() => setFetching(false));
  }, [startDate, endDate, zone, fetchZoneData, t]);

  const activeData = zone === 'norte' ? dataN : dataS;
  const hasData = !!(activeData.monthly || activeData.weekly?.length || activeData.range?.length);

  // ── Chart data formatters ──────────────────────────────────
  const monthlyChartData = activeData.monthly ? [
    { name: 'Promedio', value: parseFloat(activeData.monthly.avg_price_per_box) },
    { name: 'Mínimo',   value: parseFloat(activeData.monthly.min_price_per_box) },
    { name: 'Máximo',   value: parseFloat(activeData.monthly.max_price_per_box) },
  ] : [];

  const weeklyChartData = (activeData.weekly || []).map(item => ({
    date: formatChartDate(item.date),
    pricePerBox: parseFloat(item.price_per_box),
    precioOficial: PRECIO_OFICIAL,
  }));

  const rangeChartData = [...(activeData.range || [])]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(item => ({
      date: formatChartDate(item.date),
      pricePerBox: parseFloat(item.price_per_box),
      precioOficial: PRECIO_OFICIAL,
    }));

  // For comparative: separate per zone
  const rangeChartDataN = [...(dataN.range || [])]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(item => ({
      date: formatChartDate(item.date),
      pricePerBox: parseFloat(item.price_per_box),
      precioOficial: PRECIO_OFICIAL,
    }));
  const rangeChartDataS = [...(dataS.range || [])]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(item => ({
      date: formatChartDate(item.date),
      pricePerBox: parseFloat(item.price_per_box),
      precioOficial: PRECIO_OFICIAL,
    }));

  // ── Helpers ────────────────────────────────────────────────
  // Fetch both zones if missing; return the fresh data (closures don't
  // see updated state, so we capture it locally before generating).
  const ensureBothZones = async () => {
    let nData = dataN;
    let sData = dataS;
    const tasks = [];
    if (!nData.range?.length && !nData.monthly) {
      tasks.push(fetchZoneData('norte').then(d => { nData = d; setDataN(d); }));
    }
    if (!sData.range?.length && !sData.monthly) {
      tasks.push(fetchZoneData('sur').then(d => { sData = d; setDataS(d); }));
    }
    if (tasks.length) await Promise.all(tasks);
    // Wait for React to render the new data so chart refs are populated
    await new Promise(r => setTimeout(r, 320));
    return { norte: nData, sur: sData };
  };

  const fetchPrevMonth = async (z) => {
    if (!activeData.monthly?.year || !activeData.monthly?.month) return null;
    // Compute previous month dates
    const monthIdx = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre']
      .indexOf(String(activeData.monthly.month).toLowerCase());
    if (monthIdx === -1) return null;
    const yr = parseInt(activeData.monthly.year, 10);
    const prevMonth = monthIdx === 0 ? 12 : monthIdx; // 1-indexed
    const prevYr    = monthIdx === 0 ? yr - 1 : yr;
    try {
      const token = localStorage.getItem('access_token');
      const r = await axios.get(`${baseUrl}/prices/monthly-summary/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year: prevYr, month: prevMonth, zone: z },
      });
      return r.data;
    } catch {
      return null;
    }
  };

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3500);
  };

  // ── Card click handlers ────────────────────────────────────
  const guardDates = () => {
    if (!startDate || !endDate) {
      setError('Seleccioná un rango de fechas primero.');
      return false;
    }
    setError(null);
    return true;
  };

  const handleExecutive = async () => {
    if (!guardDates() || !hasData) return;
    setLoadingCard('executive');
    try {
      await buildExecutiveReport({
        startDate, endDate,
        monthlyData:    activeData.monthly,
        weeklyData:     activeData.weekly,
        priceRangeData: activeData.range,
        rangeChartRef,
        zoneLabel: ZONE_LABELS[zone],
      });
      flash('Reporte ejecutivo generado.');
    } catch (e) {
      console.error(e); setError('Error generando el reporte.');
    } finally { setLoadingCard(null); }
  };

  const handleDetailed = async () => {
    if (!guardDates() || !hasData) return;
    setLoadingCard('detailed');
    try {
      await buildDetailedReport({
        startDate, endDate,
        monthlyData:    activeData.monthly,
        weeklyData:     activeData.weekly,
        priceRangeData: activeData.range,
        monthlyChartRef, weeklyChartRef, rangeChartRef,
        zoneLabel: ZONE_LABELS[zone],
      });
      flash('Reporte detallado generado.');
    } catch (e) {
      console.error(e); setError('Error generando el reporte.');
    } finally { setLoadingCard(null); }
  };

  const handleComparative = async () => {
    if (!guardDates()) return;
    setLoadingCard('comparative');
    try {
      const { norte, sur } = await ensureBothZones();
      await buildComparativeReport({
        startDate, endDate,
        norteRangeData:     norte.range,
        surRangeData:       sur.range,
        norteRangeChartRef: rangeChartRefN,
        surRangeChartRef:   rangeChartRefS,
      });
      flash('Reporte comparativo generado.');
    } catch (e) {
      console.error(e); setError('Error generando el reporte.');
    } finally { setLoadingCard(null); }
  };

  const handleMonthly = async () => {
    if (!guardDates() || !activeData.monthly) {
      setError('El reporte mensual necesita un rango que cubra al menos un mes completo.');
      return;
    }
    setLoadingCard('monthly');
    try {
      const prevMonthly = await fetchPrevMonth(zone);
      await buildMonthlyReport({
        startDate, endDate,
        monthlyData:     activeData.monthly,
        prevMonthlyData: prevMonthly,
        weeklyData:      activeData.weekly,
        priceRangeData:  activeData.range,
        rangeChartRef, weeklyChartRef,
        zoneLabel: ZONE_LABELS[zone],
      });
      flash('Boletín mensual generado.');
    } catch (e) {
      console.error(e); setError('Error generando el boletín.');
    } finally { setLoadingCard(null); }
  };

  const handleExcel = async () => {
    if (!guardDates()) return;
    setLoadingCard('excel');
    const token = localStorage.getItem('access_token');
    if (!token) { setError(t('auth_required')); setLoadingCard(null); return; }
    try {
      const response = await axios.get(`${baseUrl}/prices/export/excel/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: startDate, end_date: endDate, zone },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `banaprecios_${zone}_${startDate}_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      flash('Excel descargado.');
    } catch {
      setError('Error descargando el Excel. Verifica tu suscripción.');
    } finally { setLoadingCard(null); }
  };

  const HANDLERS = {
    executive:   handleExecutive,
    detailed:    handleDetailed,
    comparative: handleComparative,
    monthly:     handleMonthly,
    excel:       handleExcel,
  };

  const isCardDisabled = (card) => {
    if (loadingCard) return true;
    if (fetching && card.needs !== 'excel') return true;
    if (card.needs === 'excel') return !startDate || !endDate;
    if (card.needs === 'monthly') return !activeData.monthly;
    if (card.needs === 'both') return !startDate || !endDate;
    return !hasData;
  };

  return (
    <div className="export-container">
      <h2>Generación de Reportes</h2>

      {/* ── Configuración ── */}
      <div className="export-section">
        <h3>Configuración</h3>

        <div className="export-config">
          <div className="export-config__field">
            <label>Zona</label>
            <div className="zone-tabs">
              <button
                className={`zone-tab ${zone === 'norte' ? 'zone-tab--active' : ''}`}
                onClick={() => setZone('norte')}
              >Zona Norte</button>
              <button
                className={`zone-tab ${zone === 'sur' ? 'zone-tab--active' : ''}`}
                onClick={() => setZone('sur')}
              >Zona Sur</button>
            </div>
            <span className="export-config__hint">El comparativo Norte vs Sur ignora esta selección y trae las dos.</span>
          </div>

          <div className="export-config__field">
            <label>Desde</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>

          <div className="export-config__field">
            <label>Hasta</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>

        {fetching && <p className="export-info">Cargando datos…</p>}
        {error    && <p className="error">{error}</p>}
        {success  && <p className="export-success">✓ {success}</p>}
      </div>

      {/* ── Tipos de Reporte ── */}
      <div className="export-section">
        <h3>Tipo de Reporte</h3>

        <div className="export-cards">
          {REPORT_CARDS.map(card => {
            const disabled = isCardDisabled(card);
            const isLoading = loadingCard === card.id;
            return (
              <button
                key={card.id}
                className={`report-card ${disabled ? 'report-card--disabled' : ''} ${isLoading ? 'report-card--loading' : ''}`}
                onClick={HANDLERS[card.id]}
                disabled={disabled}
                style={{ '--card-accent': card.accent }}
              >
                <span className="report-card__icon">{card.icon}</span>
                <span className="report-card__title">{card.title}</span>
                <span className="report-card__desc">{card.desc}</span>
                <span className="report-card__cta">
                  {isLoading ? 'Generando…' : (card.id === 'excel' ? 'Descargar' : 'Generar PDF')}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Hidden charts (off-screen for PDF capture) ── */}
      <div className="export-hidden-charts" aria-hidden="true">
        {/* Active zone: monthly */}
        {activeData.monthly && (
          <div ref={monthlyChartRef} style={{ width: CHART_W, height: CHART_H }}>
            <BarChart data={monthlyChartData} width={CHART_W} height={CHART_H}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" />
              <XAxis dataKey="name" stroke="#e8eaf0" tick={{ fill: '#9ba5bc', fontSize: 11 }} />
              <YAxis stroke="#e8eaf0" tick={{ fill: '#9ba5bc', fontSize: 11 }} />
              <Tooltip formatter={v => [`$${v.toFixed(2)}`, 'Precio']} contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: '12px', color: '#6b7285' }} />
              <Bar dataKey="value" name="Precio / Caja" radius={[4,4,0,0]}>
                {monthlyChartData.map((_, i) => (
                  <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </div>
        )}

        {/* Active zone: weekly */}
        {!!weeklyChartData.length && (
          <div ref={weeklyChartRef} style={{ width: CHART_W, height: CHART_H }}>
            <LineChart data={weeklyChartData} width={CHART_W} height={CHART_H}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" />
              <XAxis dataKey="date" stroke="#e8eaf0" tick={{ fill: '#9ba5bc', fontSize: 11 }} />
              <YAxis stroke="#e8eaf0" tick={{ fill: '#9ba5bc', fontSize: 11 }} />
              <Tooltip formatter={v => [`$${v.toFixed(2)}`, 'Precio']} contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: '12px', color: '#6b7285' }} />
              <Line type="monotone" dataKey="pricePerBox"   stroke="#17c671" strokeWidth={2} dot={false} name="Precio de Mercado" />
              <Line type="monotone" dataKey="precioOficial" stroke="#f53d6b" strokeDasharray="5 5" dot={false} name="Precio Oficial" />
            </LineChart>
          </div>
        )}

        {/* Active zone: range */}
        {!!rangeChartData.length && (
          <div ref={rangeChartRef} style={{ width: CHART_W, height: CHART_H }}>
            <LineChart data={rangeChartData} width={CHART_W} height={CHART_H}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" />
              <XAxis dataKey="date" stroke="#e8eaf0" tick={{ fill: '#9ba5bc', fontSize: 11 }} />
              <YAxis stroke="#e8eaf0" tick={{ fill: '#9ba5bc', fontSize: 11 }} />
              <Tooltip formatter={v => [`$${parseFloat(v).toFixed(2)}`, 'Precio']} contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: '12px', color: '#6b7285' }} />
              <Line type="monotone" dataKey="pricePerBox"   stroke="#17c671" strokeWidth={2} dot={false} name="Precio de Mercado" />
              <Line type="monotone" dataKey="precioOficial" stroke="#9ba5bc" strokeDasharray="4 4" dot={false} name="Precio Oficial" />
            </LineChart>
          </div>
        )}

        {/* Comparative: Norte */}
        {!!rangeChartDataN.length && (
          <div ref={rangeChartRefN} style={{ width: CHART_W, height: CHART_H }}>
            <LineChart data={rangeChartDataN} width={CHART_W} height={CHART_H}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" />
              <XAxis dataKey="date" stroke="#e8eaf0" tick={{ fill: '#9ba5bc', fontSize: 11 }} />
              <YAxis stroke="#e8eaf0" tick={{ fill: '#9ba5bc', fontSize: 11 }} />
              <Tooltip formatter={v => [`$${parseFloat(v).toFixed(2)}`, 'Norte']} contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: '12px', color: '#6b7285' }} />
              <Line type="monotone" dataKey="pricePerBox"   stroke="#17c671" strokeWidth={2} dot={false} name="Norte" />
              <Line type="monotone" dataKey="precioOficial" stroke="#9ba5bc" strokeDasharray="4 4" dot={false} name="Oficial" />
            </LineChart>
          </div>
        )}

        {/* Comparative: Sur */}
        {!!rangeChartDataS.length && (
          <div ref={rangeChartRefS} style={{ width: CHART_W, height: CHART_H }}>
            <LineChart data={rangeChartDataS} width={CHART_W} height={CHART_H}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" />
              <XAxis dataKey="date" stroke="#e8eaf0" tick={{ fill: '#9ba5bc', fontSize: 11 }} />
              <YAxis stroke="#e8eaf0" tick={{ fill: '#9ba5bc', fontSize: 11 }} />
              <Tooltip formatter={v => [`$${parseFloat(v).toFixed(2)}`, 'Sur']} contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: '12px', color: '#6b7285' }} />
              <Line type="monotone" dataKey="pricePerBox"   stroke="#2962ff" strokeWidth={2} dot={false} name="Sur" />
              <Line type="monotone" dataKey="precioOficial" stroke="#9ba5bc" strokeDasharray="4 4" dot={false} name="Oficial" />
            </LineChart>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportReport;
